import { z } from "zod";
import {
  BarChartComponentSchema,
  KpiComponentSchema,
  LineChartComponentSchema,
  PieChartComponentSchema,
  RadarChartComponentSchema,
  ScatterChartComponentSchema,
  TableComponentSchema,
  TextComponentSchema,
  VegaChartComponentSchema,
} from "./queryReport";
import {
  CanvasComponentConfigSchema,
  type ReportCanvasLayout,
} from "./reportCanvas";

/**
 * Every Report component type's fields. The stored layout is open (it keeps
 * fields it doesn't know), so these are what a field is checked against
 * before it is trusted: a write refuses a field its type doesn't have, and a
 * read lifts a nested one only when it fits.
 */
export const REPORT_COMPONENT_SCHEMAS: Readonly<Record<string, z.ZodObject>> = {
  kpi: KpiComponentSchema,
  "bar-chart": BarChartComponentSchema,
  "line-chart": LineChartComponentSchema,
  "scatter-chart": ScatterChartComponentSchema,
  "pie-chart": PieChartComponentSchema,
  "radar-chart": RadarChartComponentSchema,
  table: TableComponentSchema,
  text: TextComponentSchema,
  "vega-chart": VegaChartComponentSchema,
};

const fieldsOf = (schema: z.ZodObject) =>
  new Map<string, z.ZodType>(Object.entries(schema.shape));

const TYPE_FIELDS = new Map(
  Object.entries(REPORT_COMPONENT_SCHEMAS).map(([type, schema]) => [
    type,
    fieldsOf(schema),
  ]),
);
const SHARED_FIELDS = fieldsOf(CanvasComponentConfigSchema);

/**
 * A component with the fields written nested under `config` lifted onto it,
 * where every component type keeps them; its own fields win unless unset. No component
 * type has a `config` field, and the open component schema lets a nested one
 * through, so a component written `{ type, config: { xAxisKey } }` would
 * otherwise draw unset ("Not set up yet"). A nested field is lifted only when
 * its type has it and its value fits (a bad one would replace a working
 * default); for a type this doesn't know, only the shared fields are checked.
 */
export function normalizeReportComponent<T extends object>(component: T): T {
  const nested = "config" in component ? component.config : undefined;
  if (typeof nested !== "object" || nested === null || Array.isArray(nested))
    return component;
  const type =
    "type" in component && typeof component.type === "string"
      ? component.type
      : "";
  const typeFields = TYPE_FIELDS.get(type);
  const valid = Object.fromEntries(
    Object.entries(nested).filter(([key, value]) => {
      const field = (typeFields ?? SHARED_FIELDS).get(key);
      if (!field) return !typeFields;
      return field.safeParse(value).success;
    }),
  );
  // Its own fields win, except one left unset (null or absent) over a set one.
  const own = new Map(Object.entries(component));
  const lifted = Object.assign(
    { ...valid, ...component },
    Object.fromEntries(
      Object.entries(valid).filter(([key]) => own.get(key) == null),
    ),
  );
  Reflect.deleteProperty(lifted, "config");
  return lifted;
}

/** A layout with every component normalized ({@link normalizeReportComponent}). */
export function normalizeReportCanvasLayout(
  layout: ReportCanvasLayout,
): ReportCanvasLayout {
  if ("items" in layout)
    return {
      items: layout.items.map((item) => ({
        ...item,
        component: normalizeReportComponent(item.component),
      })),
    };
  return {
    data: layout.data.map((row) => row.map(normalizeReportComponent)),
  };
}
